import React, { FormEventHandler, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Loading } from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { toast } from '@/hooks/useToast'
import {
  clearAuthError,
  login as loginAction,
  selectAuthError,
  selectAuthLoading,
  useAppDispatch,
  useAppSelector,
} from '@/store'
import { cn } from '@/utils'
import { useNavigate } from 'react-router-dom'

import scalrLoginBackground from '@/assets/images/scalr_login_background.png'
import scalrLoginIcon from '@/assets/images/scalr_login_icon.png'
import scalrMainLogo from '@/assets/images/scalr_main_logo.svg'

interface ValidationErrors {
  username?: string
  password?: string
  general?: string
}

interface FormState {
  username: string
  password: string
  errors: ValidationErrors
  touched: {
    username: boolean
    password: boolean
  }
  isSubmitting: boolean
}

export default function LoginPage({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  const { t } = useTranslation()
  const [formState, setFormState] = useState<FormState>({
    username: '',
    password: '',
    errors: {},
    touched: {
      username: false,
      password: false,
    },
    isSubmitting: false,
  })

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isLoading = useAppSelector(selectAuthLoading)
  const error = useAppSelector(selectAuthError)

  const usernameRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    usernameRef.current?.focus()
  }, [])

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {}

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const trimmedEmail = formState.username.trim()

    if (!trimmedEmail) {
      errors.username = t('auth.validation.emailRequired')
    } else if (!emailRegex.test(trimmedEmail)) {
      errors.username = t('auth.validation.emailInvalid')
    }

    // Password validation
    const trimmedPassword = formState.password.trim()
    if (!trimmedPassword) {
      errors.password = t('auth.validation.passwordRequired')
    }

    setFormState((prev) => ({ ...prev, errors }))
    return Object.keys(errors).length === 0
  }

  // Input handlers
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormState((prev) => ({
      ...prev,
      username: value,
      errors: { ...prev.errors, username: undefined, general: undefined },
    }))
    dispatch(clearAuthError())
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFormState((prev) => ({
      ...prev,
      password: value,
      errors: { ...prev.errors, password: undefined, general: undefined },
    }))
    dispatch(clearAuthError())
  }

  const handleFieldBlur = (field: 'username' | 'password') => {
    setFormState((prev) => ({
      ...prev,
      touched: { ...prev.touched, [field]: true },
    }))

    // Validate the field on blur
    const errors: ValidationErrors = {}
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (field === 'username') {
      const trimmedEmail = formState.username.trim()
      if (!trimmedEmail) {
        errors.username = t('auth.validation.emailRequired')
      } else if (!emailRegex.test(trimmedEmail)) {
        errors.username = t('auth.validation.emailInvalid')
      }
    } else if (field === 'password') {
      const trimmedPassword = formState.password.trim()
      if (!trimmedPassword) {
        errors.password = t('auth.validation.passwordRequired')
      }
    }

    setFormState((prev) => ({
      ...prev,
      errors: { ...prev.errors, ...errors },
    }))
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    // Prevent double submission
    if (formState.isSubmitting || isLoading) {
      return
    }

    // Mark all fields as touched
    setFormState((prev) => ({
      ...prev,
      touched: { username: true, password: true },
      isSubmitting: true,
    }))

    // Validate form
    if (!validateForm()) {
      setFormState((prev) => ({ ...prev, isSubmitting: false }))
      return
    }

    try {
      const trimmedUsername = formState.username.trim()
      const trimmedPassword = formState.password.trim()

      await dispatch(
        loginAction({
          username: trimmedUsername,
          password: trimmedPassword,
        })
      ).unwrap()

      // Navigate on success
      // TODO: navigate to dashboard when it is finished
      navigate('/clarity/sales')
    } catch (error) {
      setFormState((prev) => ({ ...prev, isSubmitting: false }))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const form = e.currentTarget.closest('form')
      if (form) {
        form.requestSubmit()
      }
    }
  }

  useEffect(() => {
    if (error) {
      setFormState((prev) => ({
        ...prev,
        errors: { ...prev.errors, general: error },
        isSubmitting: false,
      }))

      toast({
        title: t('auth.errors.loginFailed'),
        description: t('auth.errors.loginFailedDescription'),
        variant: 'destructive',
      })
      dispatch(clearAuthError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (!isLoading) {
      setFormState((prev) => ({ ...prev, isSubmitting: false }))
    }
  }, [isLoading])

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-white p-6 md:p-10 relative">
      {/* Background Image Layer */}
      <div
        className="absolute inset-0 bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: `url(${scalrLoginBackground})`,
          backgroundSize: '90%',
        }}
      />

      {/* Logo in top left corner */}
      <div className="absolute top-6 left-6 md:top-10 md:left-10 z-10">
        <img
          src={scalrMainLogo}
          alt="Scalr Logo"
          className="h-6 md:h-9 w-auto"
        />
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center">
          <Loading />
        </div>
      )}

      {/* Login Card */}
      <div
        className={cn(
          'w-full max-w-md relative z-20',
          isLoading && 'blur-sm pointer-events-none'
        )}
      >
        <div className="relative">
          <div className={cn('flex flex-col gap-6', className)} {...props}>
            <Card className="aspect-square flex flex-col">
              <CardContent className="p-0 flex-1 flex items-center justify-center">
                <div className="p-6 md:p-8 w-full">
                  <form onSubmit={handleSubmit}>
                    <div className="flex flex-col gap-6">
                      <div className="flex flex-col items-center text-center">
                        <img
                          src={scalrLoginIcon}
                          alt="Scalr Login Icon"
                          className="h-16 w-16 mb-4"
                        />
                        <p className="leading-none tracking-tight text-black-500 text-display-m">
                          {t('auth.login.heading')}
                        </p>
                      </div>
                      <div className="grid gap-2">
                        <Label
                          className="leading-none tracking-tight text-black-500 text-text-m text-muted-foreground"
                          htmlFor="email"
                        >
                          {t('auth.login.email')}
                        </Label>
                        <Input
                          ref={usernameRef}
                          id="email"
                          name="email"
                          type="email"
                          placeholder={t('auth.login.emailPlaceholder')}
                          autoComplete="email"
                          required
                          value={formState.username}
                          onChange={handleUsernameChange}
                          onBlur={() => handleFieldBlur('username')}
                          onKeyDown={handleKeyDown}
                          disabled={isLoading || formState.isSubmitting}
                          className={cn(
                            formState.errors.username &&
                              formState.touched.username &&
                              'border-red-500 focus-visible:ring-red-500'
                          )}
                          aria-invalid={
                            !!formState.errors.username &&
                            formState.touched.username
                          }
                          aria-describedby={
                            formState.errors.username
                              ? 'email-error'
                              : undefined
                          }
                        />
                        {formState.errors.username &&
                          formState.touched.username && (
                            <p
                              id="email-error"
                              className="text-sm text-red-500 mt-1"
                            >
                              {formState.errors.username}
                            </p>
                          )}
                      </div>
                      <div className="grid gap-2">
                        <Label
                          htmlFor="password"
                          className="leading-none tracking-tight text-black-500 text-text-m text-muted-foreground"
                        >
                          {t('auth.login.password')}
                        </Label>
                        <Input
                          ref={passwordRef}
                          id="password"
                          name="password"
                          type="password"
                          placeholder={t('auth.login.passwordPlaceholder')}
                          autoComplete="current-password"
                          required
                          value={formState.password}
                          onChange={handlePasswordChange}
                          onBlur={() => handleFieldBlur('password')}
                          onKeyDown={handleKeyDown}
                          disabled={isLoading || formState.isSubmitting}
                          className={cn(
                            formState.errors.password &&
                              formState.touched.password &&
                              'border-red-500 focus-visible:ring-red-500'
                          )}
                          aria-invalid={
                            !!formState.errors.password &&
                            formState.touched.password
                          }
                          aria-describedby={
                            formState.errors.password
                              ? 'password-error'
                              : undefined
                          }
                        />
                        {formState.errors.password &&
                          formState.touched.password && (
                            <p
                              id="password-error"
                              className="text-sm text-red-500 mt-1"
                            >
                              {formState.errors.password}
                            </p>
                          )}
                      </div>
                      <a
                        href="https://www.scalrplatform.com/about-us"
                        target="_blank"
                        className="ml-auto text-text-m font-semibold tracking-tight underline-offset-2 text-blue-500"
                        tabIndex={isLoading ? -1 : 0}
                      >
                        {t('auth.login.forgotPassword')}
                      </a>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading || formState.isSubmitting}
                      >
                        {isLoading || formState.isSubmitting
                          ? t('auth.login.signingIn')
                          : t('auth.login.submit')}
                      </Button>
                    </div>
                  </form>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
